import React from 'react';
import { Box, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const SIZES = {
  // History modal tabs (MUI small chip)
  small: { height: 24, fontSize: 12, px: 1, radius: 12 },
  // #245 hours mode toggle
  medium: { height: 32, fontSize: 13, px: 1.75, radius: 16 },
};

/**
 * ViewSwitcher — row of chips for sub-views and segmented toggles
 * (UI Design Standards §4.3). Selected = filled primary blue with white text;
 * unselected = outlined neutral. This is the only place a filled chip is used.
 *
 * Props:
 *   options    {Array<{ value, label, disabled? }>}
 *   value      {any}    selected option value
 *   onChange   {func}   (value) => void; not called for the selected option
 *   size       'small' (default) | 'medium'
 *   ariaLabel  {string} label for the button group (e.g. "Hours date range")
 *   disabled   {bool}   disables every option
 *   sx         {object} extra styles for the row
 */
const ViewSwitcher = ({
  options = [],
  value,
  onChange,
  size = 'small',
  ariaLabel,
  disabled = false,
  sx,
}) => {
  const { ft } = useTheme().palette;
  const s = SIZES[size] || SIZES.small;

  return (
    <Box
      role="group"
      aria-label={ariaLabel}
      sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ...sx }}
    >
      {options.map((option) => {
        const selected = option.value === value;
        const isDisabled = disabled || !!option.disabled;
        return (
          <Chip
            key={String(option.value)}
            label={option.label}
            aria-pressed={selected}
            disabled={isDisabled}
            onClick={() => {
              // MUI's disabled Chip only blocks the mouse via CSS; guard here too.
              if (!selected && !isDisabled && onChange) onChange(option.value);
            }}
            sx={{
              flexShrink: 0,
              height: s.height,
              fontSize: s.fontSize,
              fontWeight: 500,
              textTransform: 'none',
              borderRadius: `${s.radius}px`,
              border: '1px solid',
              borderColor: selected ? ft.primary.main : ft.neutrals.fieldBorder,
              backgroundColor: selected ? ft.primary.main : ft.neutrals.white,
              color: selected ? ft.neutrals.white : ft.neutrals.textBody,
              '& .MuiChip-label': { px: s.px },
              '&.MuiChip-clickable:hover': {
                backgroundColor: selected ? ft.primary.dark : ft.neutrals.tile,
                borderColor: selected ? ft.primary.dark : ft.neutrals.fieldBorder,
              },
              '&.Mui-focusVisible': {
                backgroundColor: selected ? ft.primary.dark : ft.neutrals.tile,
                outline: `2px solid ${ft.primary.main}`,
                outlineOffset: 2,
              },
              '&.MuiChip-clickable:active': { boxShadow: 'none' },
            }}
          />
        );
      })}
    </Box>
  );
};

export default ViewSwitcher;
